import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type CampaignInfluenceResult } from '@whinerp/client';
import { formatMoney, todayIn } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Campaign influence on revenue (G-176). Said beside the figures: they
 * overlap, because every campaign that touched a deal counts it.
 */
export function InfluencePanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = useEntitlement('F-080').enabled && can('analytics.view');
  const today = todayIn(format);
  const [days, setDays] = useState(90);
  const start = new Date(Date.parse(today) - days * 86_400_000).toISOString().slice(0, 10);
  const report = useQuery<CampaignInfluenceResult, ApiError>({
    queryKey: ['campaign-influence', start, today],
    queryFn: () => api.campaignInfluence({ query: { periodStart: start, periodEnd: today } }),
    enabled,
    placeholderData: (previous) => previous,
  });
  if (!enabled || !report.data) return null;
  const view = report.data;
  const money = (amount: string) => formatMoney(amount, format, { currency: view.currency });
  return (
    <Panel data-region="campaign-influence">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('influence.title')}</SectionLabel>
          <select className="ms-auto h-input rounded-control border border-control bg-surface px-2 text-body text-ink" data-influence-days
            aria-label={t('influence.period')} value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {[90, 180, 365].map((value) => <option key={value} value={value}>{t('influence.lastDays', { values: { days: String(value) } })}</option>)}
          </select>
        </div>
        <p className="text-meta text-ink-meta">{t('influence.help', { values: { won: money(view.wonValue), deals: String(view.wonDeals) } })}</p>
        {view.campaigns.length === 0 ? (
          <p className="text-body text-ink-meta" data-influence-empty>{t('influence.none')}</p>
        ) : (
          <table className="w-full text-dense" data-influence>
            <thead>
              <tr className="text-meta text-ink-meta">
                <th className="p-1 text-start font-normal">{t('influence.campaign')}</th>
                <th className="p-1 text-end font-normal">{t('influence.engaged')}</th>
                <th className="p-1 text-end font-normal">{t('influence.reached')}</th>
              </tr>
            </thead>
            <tbody>
              {view.campaigns.map((row) => (
                <tr key={row.campaignId} data-influence-campaign={row.campaignId}>
                  <td className="p-1 text-ink">{row.name}</td>
                  <td className="p-1 text-end text-ink" data-influence-engaged>
                    {money(row.engagedValue)} <span className="text-ink-meta">({row.engagedDeals})</span>
                  </td>
                  <td className="p-1 text-end text-ink-600" data-influence-reached>
                    {money(row.reachedValue)} <span className="text-ink-meta">({row.reachedDeals})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Panel>
  );
}
